import React, { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Trash2Icon } from "lucide-react";
import Label from "../../../../../components/form_fields/Label";
import { PAY_PERIOD, STATUS } from "../../../../../models/employee.dto";
import {
	APP_CONFIG,
	compareDateFields,
	enumToStringConverter,
	validatePast,
} from "../../../../../helpers/appHelpers";
import APISelectField from "../../../../../components/form_fields/APISelectField";
import MoneyInput from "../../../../../components/form_fields/MoneyInput";
import { EmployeeFormContext } from "../../EmployeeFormContext";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import { Notch } from "../../../../../models/notches.dto";
import API from "../../../../../services/utils/api";
import { useAsyncEffect, useLockFn } from "ahooks";
import useUserStore from "../../../../../state-management/useUserStore";
import { PATHS } from "../../../../../routes/routes.paths";

interface IFormInputs {
	employeeNumber: string;
	startDate: string;
	jobTitleId: number;
	departmentId: number;
	stationId: number;
	divisionId: number;
	costAreaId?: number;
	status: STATUS;
	employmentDate: string;
	terminationDate: string;
	notchId?: string;
	payPeriod: PAY_PERIOD;
	basicPay: number;
	currencyId: number;
	hr?: string;
	pensioner?: boolean;
	excludeFromPayrollRun?: boolean;
}

interface IEmploymentDetailsProps {
	formData: any;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	setView: (view: string) => void;
	reloadTableData?: () => void;
	closeForm?: () => void;
}

interface ClearFieldButtonProps {
	onClear: () => void;
	disabled?: boolean;
	show?: boolean;
	fieldName?: string;
}

const ClearFieldButton: React.FC<ClearFieldButtonProps> = ({
	onClear,
	disabled = false,
	show = true,
	fieldName = "field",
}) => {
	if (!show) return null;

	return (
		<div className="relative group">
			<button
				type="button"
				onClick={onClear}
				disabled={disabled}
				className="text-gray-600 hover:text-red-500 hover:bg-gray-100 rounded-full p-1 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
				aria-label={`Clear ${fieldName}`}
			>
				<Trash2Icon size={18} strokeWidth={2.5} />
			</button>
			{/* Tooltip */}
			<div className="absolute right-0 top-full mt-1 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20 pointer-events-none">
				Clear {fieldName}
			</div>
		</div>
	);
};

const EmploymentDetails = ({
	formData,
	setView,
	setFormData,
	reloadTableData,
	closeForm,
}: IEmploymentDetailsProps) => {
	const { userType } = useUserStore();
	const { currentCompany } = useCompanyStore();
	const {
		companyCurrencies,
		jobTitles,
		CostCenterData,
		DepartmentData,
		DivisionData,
		StationData,
	} = useContext(EmployeeFormContext);

	const {
		register,
		watch,
		setValue,
		handleSubmit,
		clearErrors,
		formState: { errors },
	} = useForm<IFormInputs>();

	useEffect(() => {
		const { unsubscribe } = watch((values) => {
			setFormData((prev) => ({ ...prev, ...values }));
		});
		return () => unsubscribe();
	}, [watch]);

	const [jobTitleNotches, setJobTitleNotches] = useState<Notch[]>([]);
	const [jobTitleSelected, setJobTitleSelected] = useState(false);

	const handleJobTitleChange = async (id: string) => {
		if (!id) {
			setJobTitleNotches([]);
			setJobTitleSelected(false);
			return;
		}

		const jobTitle = jobTitles.find((jobTitle) => jobTitle.id === Number(id));

		try {
			const notches = await API.ProcessingCompanyAPI.getNotch({
				companyId: currentCompany?.id,
				limit: APP_CONFIG.PAGE_LIMIT,
				employeeBandId: jobTitle.employeeBandId,
			});

			setJobTitleNotches(notches.data);
			setJobTitleSelected(true);
		} catch {}
	};

	// Clear field handlers
	const handleClearField = (fieldName: keyof IFormInputs) => {
		setValue(fieldName as any, undefined);
		clearErrors(fieldName as any);
		setFormData((prev: any) => ({
			...prev,
			[fieldName]: null,
		}));
	};

	const handleClearJobTitle = () => {
		setValue("jobTitleId" as any, undefined);
		setValue("notchId" as any, undefined);
		clearErrors("jobTitleId" as any);
		clearErrors("notchId" as any);
		setFormData((prev: any) => ({
			...prev,
			jobTitleId: null,
			notchId: null,
		}));
		setJobTitleNotches([]);
		setJobTitleSelected(false);
	};

	useAsyncEffect(
		useLockFn(async () => {
			if (formData?.jobTitleId && !jobTitleNotches.length) {
				await handleJobTitleChange(formData.jobTitleId);
			}
		}),
		[],
	);

	useEffect(() => {
		if (formData?.notchId && jobTitleNotches.length) {
			setValue("notchId", formData.notchId);
		}
	}, [jobTitleNotches]);

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setFormData((prev: any) => ({ ...prev, ...data }));
		setView("paymentInfo");
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="grid grid-cols-2 gap-x-4 gap-y-6 mt-6">
				<div className="w-full">
					<Label title="Staff ID" for="employeeNumber" />
					<input
						defaultValue={formData?.employeeNumber}
						placeholder={"Enter staff ID"}
						type="text"
						{...register("employeeNumber", {
							required: "Enter staff ID here",
						})}
						id="employeeNumber"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.employeeNumber
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.employeeNumber && (
						<p className="text-red-500 text-sm mt-1">
							{errors.employeeNumber.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<div className="flex items-center justify-between mb-2">
						<Label title="Department" for="departmentId" optional />
						<ClearFieldButton
							show={!!watch("departmentId")}
							onClear={() => handleClearField("departmentId")}
							fieldName="Department"
						/>
					</div>
					<APISelectField
						key="departmentId"
						attributes={{
							id: "departmentId",
							defaultValue: formData?.departmentId,
						}}
						placeholder="Select Department"
						register={register}
						name="departmentId"
						options={DepartmentData}
						pathname={PATHS.SUB_COMPANY_SETTINGS.DEPARTMENTS}
						required={false}
						error={errors.departmentId}
						valueAsNumber
						doNotLinkToResource={userType === "HR"}
					/>
					{errors.departmentId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.departmentId.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<div className="flex items-center justify-between mb-2">
						<Label title="Station" for="stationId" optional />
						<ClearFieldButton
							show={!!watch("stationId")}
							onClear={() => handleClearField("stationId")}
							fieldName="Station"
						/>
					</div>
					<APISelectField
						key="stationId"
						attributes={{
							id: "stationId",
							defaultValue: formData?.stationId,
						}}
						placeholder="Select Station"
						register={register}
						name="stationId"
						options={StationData}
						pathname={PATHS.SUB_COMPANY_SETTINGS.STATIONS}
						required={false}
						error={errors.stationId}
						doNotLinkToResource={userType === "HR"}
					/>
					{errors.stationId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.stationId.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<div className="flex items-center justify-between mb-2">
						<Label title="Division" for="division" optional />
						<ClearFieldButton
							show={!!watch("divisionId")}
							onClear={() => handleClearField("divisionId")}
							fieldName="Division"
						/>
					</div>
					<APISelectField
						key="divisionId"
						attributes={{
							id: "divisionId",
							defaultValue: formData?.divisionId,
						}}
						placeholder="Select Division"
						register={register}
						name="divisionId"
						options={DivisionData}
						pathname={PATHS.SUB_COMPANY_SETTINGS.DIVISIONS}
						required={false}
						error={errors.divisionId}
						valueAsNumber
						doNotLinkToResource={userType === "HR"}
					/>
					{errors.divisionId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.divisionId.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<div className="flex items-center justify-between mb-2">
						<Label title="Cost Centre" for="costCentre" optional />
						<ClearFieldButton
							show={!!watch("costAreaId")}
							onClear={() => handleClearField("costAreaId")}
							fieldName="Cost Centre"
						/>
					</div>
					<APISelectField
						key="costAreaId"
						attributes={{
							id: "costAreaId",
							defaultValue: formData?.costAreaId,
						}}
						placeholder="Select Cost Centre"
						register={register}
						name="costAreaId"
						options={CostCenterData}
						pathname={PATHS.SUB_COMPANY_SETTINGS.COST_CENTERS}
						required={false}
						error={errors.costAreaId}
						valueAsNumber
						doNotLinkToResource={userType === "HR"}
					/>
					{errors.costAreaId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.costAreaId.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Employment Status" for="status" />
					<select
						defaultValue={formData?.status}
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.status
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						{...register("status", {
							required: "Select contract status here",
						})}
					>
						<option value="">Select Option</option>
						{Object.entries(STATUS).map(([key, value]) => (
							<option key={key} value={value}>
								{enumToStringConverter(value)}
							</option>
						))}
					</select>
					{errors.status && (
						<p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Employment Date" for="employmentDate" />
					<input
						defaultValue={formData?.employmentDate?.slice(0, 10)}
						placeholder={"mm/dd/yyyy"}
						type="date"
						{...register("employmentDate", {
							required: "Enter contract start date here",
							validate: validatePast,
						})}
						id="employmentDate"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.employmentDate
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.employmentDate && (
						<p className="text-red-500 text-sm mt-1">
							{errors.employmentDate.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<div className="flex items-center justify-between mb-2">
						<Label title="Termination Date" for="terminationDate" optional />
						<ClearFieldButton
							show={!!watch("terminationDate")}
							onClear={() => handleClearField("terminationDate")}
							fieldName="Termination Date"
						/>
					</div>
					<input
						defaultValue={formData?.terminationDate?.slice(0, 10) || ""}
						placeholder={"mm/dd/yyyy"}
						type="date"
						{...register("terminationDate", {
							required:
								formData?.status === STATUS.TERMINATED
									? "Enter termination date here"
									: false,
							validate: (value) => {
								const employmentDate = compareDateFields(
									watch("employmentDate"),
									value,
									"Termination date must be greater than employment date",
								);

								if (employmentDate === true) {
									return validatePast(value);
								}

								return employmentDate;
							},
						})}
						id="terminationDate"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 text-sm  ${
							errors.terminationDate
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.terminationDate && (
						<p className="text-red-500 text-sm mt-1">
							{errors.terminationDate.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<div className="flex items-center justify-between mb-2">
						<Label title="Job Title" for="jobTitleId" optional />
						<ClearFieldButton
							show={!!watch("jobTitleId")}
							onClear={handleClearJobTitle}
							fieldName="Job Title"
						/>
					</div>
					<APISelectField
						key="jobTitleId"
						attributes={{
							id: "jobTitleId",
							defaultValue: formData?.jobTitleId,
							onInput: (e: any) => handleJobTitleChange(e.target.value),
						}}
						placeholder="Select Job Title"
						register={register}
						name="jobTitleId"
						options={jobTitles}
						pathname={PATHS.SUB_COMPANY_SETTINGS.JOB_TITLES}
						required={false}
						error={errors.jobTitleId}
						valueAsNumber
						doNotLinkToResource={userType === "HR"}
					/>
				</div>
				<div className="w-full">
					<div className="flex items-center justify-between mb-2">
						<Label title="Notch" for="notchId" optional />
						<ClearFieldButton
							show={!!watch("notchId") && jobTitleSelected}
							onClear={() => handleClearField("notchId")}
							disabled={!jobTitleSelected}
							fieldName="Notch"
						/>
					</div>
					<select
						defaultValue={formData?.notchId}
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 text-sm border-none`}
						{...register("notchId", {
							required: false,
							valueAsNumber: true,
						})}
						disabled={!jobTitleSelected}
					>
						<option value="">Select Notch</option>
						{jobTitleNotches.map((item) => (
							<option key={item?.id} value={item?.id}>
								{item?.code}
							</option>
						))}
					</select>
				</div>
				<div className="col-span-2 flex space-x-4 pb-3">
					<h3>Human Resource Personnel?</h3>
					<div className="flex space-x-3 items-center">
						<input
							defaultChecked={formData?.hr === true || formData?.hr === "yes"}
							{...register("hr", {
								required: false,
							})}
							type="radio"
							id="yes"
							value="yes"
						/>
						<label htmlFor="yes">Yes</label>
					</div>
					<div className="flex space-x-3 items-center">
						<input
							defaultChecked={formData?.hr === false || formData?.hr === "no"}
							{...register("hr", {
								required: false,
							})}
							type="radio"
							id="no"
							value="no"
						/>
						<label htmlFor="no">No</label>
					</div>
				</div>
				<div className="w-full">
					<Label title="Basic Monthly Rate" for="basicPay" />
					<div className="flex items-center">
						<MoneyInput
							attributes={{
								id: "basicPay",
								placeholder: "0",
							}}
							register={register}
							name="basicPay"
							setValue={setValue}
							defaultValue={formData?.basicPay}
							required="Enter basic monthly rate here"
							error={errors.basicPay}
						/>
						<select
							defaultValue={formData?.currencyId || ""}
							{...register("currencyId", {
								required: "Select currency",
								valueAsNumber: true,
							})}
							className="w-[120px] dark"
						>
							<option value="">Select</option>
							{companyCurrencies?.map((item) => (
								<option key={item?.id} value={item?.id}>
									{item?.currency?.name} ({item?.currency?.code})
								</option>
							))}
						</select>
					</div>
					{(errors.basicPay || errors.currencyId) && (
						<p className="text-red-500 text-sm mt-1">
							{errors.basicPay?.message || errors.currencyId?.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Payment Period" for="payPeriod" />
					<select
						defaultValue={formData?.payPeriod}
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.payPeriod
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						{...register("payPeriod", {
							required: "Please select an option here",
						})}
					>
						<option value="">Select Payment Period</option>
						<option value="HOUR">Hour</option>
						<option value="DAY">Day</option>
						<option value="WEEK">Week</option>
						<option value="MONTH">Month</option>
						<option value="QUARTER">Quarter</option>
						<option value="YEAR">Year</option>
					</select>
					{errors.payPeriod && (
						<p className="text-red-500 text-sm mt-1">
							{errors.payPeriod.message}
						</p>
					)}
				</div>
				<div className="col-span-2 flex space-x-3 items-center">
					<input
						type="checkbox"
						{...register("pensioner", {
							required: false,
						})}
						id="pensioner"
						defaultChecked={formData.pensioner}
					/>
					<label htmlFor="pensioner">Do Not Deduct Pension Contributions</label>
				</div>

				<div className="col-span-2 flex space-x-3 items-center">
					<input
						type="checkbox"
						{...register("excludeFromPayrollRun", { required: false })}
						id="excludeFromPayrollRun"
						defaultChecked={formData.excludeFromPayrollRun}
					/>
					<label htmlFor="excludeFromPayrollRun">
						Exclude from Payroll run
					</label>
				</div>
			</div>
			<div className="w-full flex items-center justify-end gap-4 mt-10">
				<button
					onClick={() => setView("personalDetails")}
					className="h-14 px-8 border border-gray-300 rounded-lg text-sm text-gray-600"
				>
					Go Back
				</button>
				<button
					onClick={handleSubmit(onSubmit)}
					className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
				>
					Continue
				</button>
			</div>
		</form>
	);
};

export default EmploymentDetails;
